import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { validateWarehouseData, formatDimensions } from '../../utils/warehouseUtils';
import type { Warehouse, WarehouseUpdateData, MeasurementUnit } from '../../types/warehouse';
import {
  Save,
  Trash2,
  AlertTriangle,
  X,
  Warehouse as WarehouseIcon,
  Ruler,
  Calendar,
  AlertCircle,
  CheckCircle,
  Info,
  Palette,
  Grid3X3,
} from 'lucide-react';

interface WarehouseDetailsTabProps {
  warehouse: Warehouse;
  statistics: {
    total_zones: number;
    total_racks: number;
    total_bins: number;
  };
  onUpdate: (data: WarehouseUpdateData) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}

export const WarehouseDetailsTab: React.FC<WarehouseDetailsTabProps> = ({
  warehouse,
  statistics,
  onUpdate,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: warehouse.name,
    unit: warehouse.unit as MeasurementUnit,
    length: warehouse.length,
    width: warehouse.width,
    height: warehouse.height,
    border_color: warehouse.border_color || '#2563eb',
    map_unit: (warehouse.map_unit || warehouse.unit) as MeasurementUnit,
    grid_unit: (warehouse.grid_unit || warehouse.unit) as MeasurementUnit,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const changed =
      formData.name !== warehouse.name ||
      formData.unit !== warehouse.unit ||
      formData.length !== warehouse.length ||
      formData.width !== warehouse.width ||
      formData.height !== warehouse.height ||
      formData.border_color !== (warehouse.border_color || '#2563eb') ||
      formData.map_unit !== (warehouse.map_unit || warehouse.unit) ||
      formData.grid_unit !== (warehouse.grid_unit || warehouse.unit);
    setHasChanges(changed);
  }, [formData, warehouse]);

  useEffect(() => {
    setFormData({
      name: warehouse.name,
      unit: warehouse.unit as MeasurementUnit,
      length: warehouse.length,
      width: warehouse.width,
      height: warehouse.height,
      border_color: warehouse.border_color || '#2563eb',
      map_unit: (warehouse.map_unit || warehouse.unit) as MeasurementUnit,
      grid_unit: (warehouse.grid_unit || warehouse.unit) as MeasurementUnit,
    });
  }, [warehouse]);

  const handleUnitChange = (newUnit: MeasurementUnit) => {
    if (newUnit === formData.unit) return;

    const conversionFactor = newUnit === 'cm' ? 100 : 0.01;
    setFormData({
      ...formData,
      unit: newUnit,
      length: Math.round(formData.length * conversionFactor * 100) / 100,
      width: Math.round(formData.width * conversionFactor * 100) / 100,
      height: formData.height ? Math.round(formData.height * conversionFactor * 100) / 100 : null,
    });
  };

  const handleSave = async () => {
    const validation = validateWarehouseData(formData);
    if (!validation.isValid) {
      const errorMap: Record<string, string> = {};
      validation.errors.forEach(err => {
        errorMap[err.field] = err.message;
      });
      setErrors(errorMap);
      return;
    }

    setErrors({});
    setSaving(true);

    const success = await onUpdate(formData);

    setSaving(false);

    if (success) {
      setSuccessMessage('Warehouse settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const success = await onDelete();
    setDeleting(false);

    if (success) {
      navigate('/settings');
    }
  };

  const handleReset = () => {
    setFormData({
      name: warehouse.name,
      unit: warehouse.unit as MeasurementUnit,
      length: warehouse.length,
      width: warehouse.width,
      height: warehouse.height,
      border_color: warehouse.border_color || '#2563eb',
      map_unit: (warehouse.map_unit || warehouse.unit) as MeasurementUnit,
      grid_unit: (warehouse.grid_unit || warehouse.unit) as MeasurementUnit,
    });
    setErrors({});
  };

  const hasZonesOrRacks = statistics.total_zones > 0 || statistics.total_racks > 0;

  return (
    <div className="space-y-8">
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-green-800 dark:text-green-300">{successMessage}</span>
        </div>
      )}

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <WarehouseIcon className="h-5 w-5 text-blue-600" />
          Basic Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Warehouse Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
              } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Created
            </label>
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">
                {new Date(warehouse.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Ruler className="h-5 w-5 text-blue-600" />
          Dimensions
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement Unit
            </label>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <button
                type="button"
                onClick={() => handleUnitChange('m')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.unit === 'm'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">Meters (m)</div>
              </button>
              <button
                type="button"
                onClick={() => handleUnitChange('cm')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.unit === 'cm'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">Centimeters (cm)</div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Length
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.length}
                  onChange={(e) => setFormData({ ...formData, length: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step={formData.unit === 'm' ? '0.1' : '1'}
                  className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                    errors.length
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  {formData.unit}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Width
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step={formData.unit === 'm' ? '0.1' : '1'}
                  className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                    errors.width
                      ? 'border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  {formData.unit}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ceiling Height <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.height || ''}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value ? parseFloat(e.target.value) : null })}
                  min="0"
                  step={formData.unit === 'm' ? '0.1' : '1'}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  {formData.unit}
                </span>
              </div>
            </div>
          </div>

          {(errors.length || errors.width) && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {errors.length || errors.width}
            </p>
          )}

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-gray-100">Floor Area:</strong>
              <span className="ml-2">
                {(formData.length * formData.width).toLocaleString()} {formData.unit}
                <sup>2</sup>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Palette className="h-5 w-5 text-blue-600" />
          Map Display Settings
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Border Color
            </label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="color"
                  value={formData.border_color}
                  onChange={(e) => setFormData({ ...formData, border_color: e.target.value })}
                  className="w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer overflow-hidden"
                  style={{ padding: 0 }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {['#2563eb', '#059669', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#475569', '#000000'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, border_color: color })}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      formData.border_color === color
                        ? 'border-gray-900 dark:border-white scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              This color is used for the warehouse boundary outline on the map
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Grid3X3 className="h-4 w-4 inline mr-1" />
                Map Display Unit
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, map_unit: 'm' })}
                  className={`px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                    formData.map_unit === 'm'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  }`}
                >
                  Meters (m)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, map_unit: 'cm' })}
                  className={`px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                    formData.map_unit === 'cm'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  }`}
                >
                  Centimeters (cm)
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Unit shown for coordinates and dimensions
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Grid3X3 className="h-4 w-4 inline mr-1" />
                Grid Display Unit
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, grid_unit: 'm' })}
                  className={`px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                    formData.grid_unit === 'm'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  }`}
                >
                  Meters (m)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, grid_unit: 'cm' })}
                  className={`px-4 py-2.5 rounded-lg border-2 transition-all text-sm ${
                    formData.grid_unit === 'cm'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  }`}
                >
                  Centimeters (cm)
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Unit used for grid line spacing and labels
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600" />
          Statistics
        </h3>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {statistics.total_zones}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Zones</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {statistics.total_racks}
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">Racks</div>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
              {statistics.total_bins}
            </div>
            <div className="text-sm text-amber-700 dark:text-amber-300">Bins</div>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="danger"
          onClick={() => setShowDeleteModal(true)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Warehouse
        </Button>

        <div className="flex gap-3">
          {hasChanges && (
            <Button variant="secondary" onClick={handleReset}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteWarehouseModal
          warehouseName={warehouse.name}
          hasContent={hasZonesOrRacks}
          statistics={statistics}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          deleting={deleting}
        />
      )}
    </div>
  );
};

interface DeleteWarehouseModalProps {
  warehouseName: string;
  hasContent: boolean;
  statistics: {
    total_zones: number;
    total_racks: number;
    total_bins: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

const DeleteWarehouseModal: React.FC<DeleteWarehouseModalProps> = ({
  warehouseName,
  hasContent,
  statistics,
  onConfirm,
  onCancel,
  deleting,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const canDelete = confirmText.toLowerCase() === 'delete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Delete Warehouse</h2>
                <p className="text-red-100 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            You are about to permanently delete <strong className="text-gray-900 dark:text-gray-100">"{warehouseName}"</strong>.
          </p>

          {hasContent && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
                This warehouse contains:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                {statistics.total_zones > 0 && (
                  <li>{statistics.total_zones} zone{statistics.total_zones !== 1 ? 's' : ''}</li>
                )}
                {statistics.total_racks > 0 && (
                  <li>{statistics.total_racks} rack{statistics.total_racks !== 1 ? 's' : ''}</li>
                )}
                {statistics.total_bins > 0 && (
                  <li>{statistics.total_bins} bin{statistics.total_bins !== 1 ? 's' : ''}</li>
                )}
              </ul>
              <p className="text-sm text-red-800 dark:text-red-300 mt-2">
                All associated data will be permanently deleted.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">delete</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="delete"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirm}
              disabled={!canDelete}
              loading={deleting}
              className="flex-1"
            >
              Delete Warehouse
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
